import { HI_EXPOSURE_LEVELS } from '@/modules/DataAnalysis/HeatIndex/hiExposure';

// Per-risk copy and configuration. The live GET /risk catalog only returns
// id/provider/indicator/threshold/window/screen_offset/op_selectable —
// everything a user actually reads (label, presets, chart title, table
// columns, copy) lives here and is merged onto the catalog entry in
// adapters.js#normalizeCatalog.
export const RISK_PRESENTATION = {
    freeze: {
        order: 1,
        label: 'Freeze',
        unit: '°F',
        unitCode: 'F',
        thresholdType: 'temperature',
        thresholdLabel: 'Threshold temperature (°F)',
        thresholdRange: { min: -20, max: 40, step: 1 },
        presets: [32, 28, 25, 22, 20],
        // Overrides the catalog's "0 degC" (= 32°F) default — the design's
        // active preset and the agreed default is 28°F. See adapters.js.
        defaultThreshold: 28,
        // The API still accepts (and defaults to) "weekly" without this, but
        // stating it here means the request shape follows the indicator
        // rather than a hardcoded string in Risk.vue — see requestParams().
        aggregation: 'weekly',
        weekly: true,
        chartTitle: 'Freeze Probability by Week (%)',
        legendLabel: 'Freeze probability',
        eventPhrase: 'at least one night below',
        loadingNoun: 'daily minima',
        emptyTitle: 'Freeze Risk Probability',
        noDataTitle: 'No daily minima returned for this period',
        emptyBody: 'Drop a pin and pick a period to see how often the record shows at least one night below your threshold temperature — counted across the historical ACIS / PRISM record, bucket by bucket.',
        coverage: 'conus',
        detailColumns: [
            { key: 'mean_min_temp', label: 'Mean min temp (°F)', decimals: 1 },
            { key: 'record_low.value', label: 'Record low (°F)', decimals: 1 },
        ],
        csvColumns: [
            { path: 'mean_min_temp', header: 'mean_min_temp_F' },
            { path: 'record_low.value', header: 'record_low_F' },
            { path: 'record_low.year', header: 'record_low_year' },
        ],
        csvTitle: 'Freeze Risk — weekly probability',
    },

    // Drought Index (catalog id stays "arid"). The only indicator with a </>
    // direction toggle (op_selectable, riskPresentation's own flag mirroring
    // the catalog's — see adapters.js) — ">" asks "how dry" (mean ARID above
    // the threshold), "<" asks "how wet" (mean ARID below it). Everything
    // that reads differently by direction lives in byOp; everything else is
    // flat. getters.js#activeIndicator is the one place that merges byOp[op]
    // over this base entry, so every component below it (chart, table,
    // summary, message card, CSV) reads flat presentation.* fields and never
    // has to know a toggle exists.
    arid: {
        order: 3,
        label: 'Drought Index',
        unit: null,
        unitCode: null,
        thresholdType: 'index',
        thresholdLabel: 'Average ARID',
        thresholdRange: { min: 0, max: 1, step: 0.05 },
        // ISSE preset ladder. Every value sits inside thresholdRange (0..1) and on its
        // 0.05 step, so a preset click and a typed value can never disagree.
        presets: [0.2, 0.4, 0.5, 0.6, 0.8],
        // Mirrors the live catalog's own default ("0.5"), so it is not an override the
        // way freeze's/chill_hours's are. It has to be stated here anyway: the catalog is
        // seeded synchronously from this table (state.js), and leaving it undefined made
        // the first Drought request go out with threshold=null — which axios drops from
        // the query string entirely, so the backend answered 400 for a missing required
        // parameter and the tab showed a generic error.
        defaultThreshold: 0.5,
        aggregation: 'weekly',
        weekly: true,
        opSelectable: true,
        defaultOp: '>',
        loadingNoun: 'the daily ARID index',
        // arid has no PRISM/CONUS coverage restriction.
        coverage: 'global',
        byOp: {
            '>': {
                chartTitle: 'Drought Probability by Week (%)',
                legendLabel: 'Drought probability',
                eventPhrase: 'an average ARID index above',
                emptyTitle: 'Drought Risk Probability',
                noDataTitle: 'No ARID index values returned for this period',
                emptyBody: 'Drop a pin and pick a period to see how often the record shows an average ARID index above your threshold — counted across the historical record, week by week.',
                detailColumns: [
                    { key: 'mean_peak_arid', label: 'Mean ARID', decimals: 2 },
                    { key: 'record_arid.value', label: 'Highest ARID', decimals: 2 },
                ],
                csvTitle: 'Drought Index — weekly probability (drier than threshold)',
            },
            '<': {
                chartTitle: 'Wet Period Probability by Week (%)',
                legendLabel: 'Wet period probability',
                eventPhrase: 'an average ARID index below',
                emptyTitle: 'Wet Period Probability',
                noDataTitle: 'No ARID index values returned for this period',
                emptyBody: 'Drop a pin and pick a period to see how often the record shows an average ARID index below your threshold — counted across the historical record, week by week.',
                detailColumns: [
                    { key: 'mean_peak_arid', label: 'Mean ARID', decimals: 2 },
                    { key: 'record_arid.value', label: 'Lowest ARID', decimals: 2 },
                ],
                csvTitle: 'Drought Index — weekly probability (wetter than threshold)',
            },
        },
        csvColumns: [
            { path: 'mean_peak_arid', header: 'mean_peak_arid' },
            { path: 'record_arid.value', header: 'record_arid' },
            { path: 'record_arid.year', header: 'record_arid_year' },
        ],
    },

    // Period-only (no weekly detail — see Risk.html, which hides the chart and the
    // weekly table for any indicator with weekly: false): the whole season's chill
    // hours total, compared against `hours`. Sourced server-side from the same
    // POST /model/chillH the Indicators tab uses, so the 32-45°F band always agrees
    // with that tab — see the API's risk/README.md.
    chill_hours: {
        order: 4,
        label: 'Chill Hours',
        unit: 'h',
        unitCode: null,
        thresholdType: 'hours',
        thresholdLabel: 'Hours',
        thresholdRange: { min: 0, max: 3000, step: 25 },
        presets: [200, 400, 600, 800, 1000],
        // Mirrors the live catalog's own default ("400") — stated explicitly for the
        // same reason arid's is (see its own comment above): the catalog is seeded
        // synchronously from this table, and an undefined default here would send the
        // first Chill Hours request out with threshold=null.
        defaultThreshold: 400,
        aggregation: 'period',
        weekly: false,
        chartTitle: null,
        legendLabel: null,
        eventPhrase: 'a season total above',
        loadingNoun: 'daily chill hours',
        emptyTitle: 'Chill Hours Probability',
        noDataTitle: 'No chill hours data returned for this period',
        emptyBody: 'Drop a pin and pick a period to see how often the season’s total chill hours (32–45°F) exceed your threshold — counted across the historical record.',
        coverage: 'conus',
        csvTitle: 'Chill Hours — period probability',
    },

    // Heat Index — one exposure condition drives every number here:
    //   intensity   the NWS level (or above)          -> `category`
    //   duration    N hours per day at that level     -> `min_hours`
    //   persistence D consecutive days like that      -> the threshold
    // The period answers "did the season hold such a stretch"; each week answers "was
    // this week part of one". With D = 1 that is simply "did it ever happen".
    // Computed server-side from the same POST /model/hi the Indicators tab uses; the
    // hourly record starts in 2019, so the sample is 7 years (small_sample_min=7).
    hi: {
        order: 5,
        label: 'Heat Index',
        unit: 'days',
        unitCode: null,
        thresholdType: 'days',
        thresholdLabel: 'Consecutive days (D)',
        thresholdRange: { min: 1, max: 30, step: 1 },
        presets: [1, 2, 3, 5, 7],
        // Mirrors the live catalog's own default ("2 d").
        defaultThreshold: 2,
        aggregation: 'weekly',
        weekly: true,
        categorySelectable: true,
        defaultCategory: 'danger',
        categories: HI_EXPOSURE_LEVELS.map(level => ({
            id: level.id,
            label: level.label,
            hint: `Heat Index of ${level.thresholdF}°F or more`,
        })),
        minHoursSelectable: true,
        defaultMinHours: 6,
        minHoursRange: { min: 1, max: 24, step: 1 },
        minHoursLabel: 'Minimum hours per day (N)',
        legendLabel: 'Heat Index probability',
        loadingNoun: 'daily Heat Index hours',
        emptyTitle: 'Heat Index Risk Probability',
        noDataTitle: 'No Heat Index values returned for this period',
        emptyBody: 'Drop a pin, pick a period and an exposure condition to see how often the record shows a stretch of consecutive days reaching it — counted across the hourly record since 2019.',
        coverage: 'conus',
        showEventYears: true,
        // Plain-language guide, shown above the chart (Risk.html).
        howToRead: 'Each bar is the share of the last years in which that week was part of a stretch of consecutive days meeting the condition above. A stretch that runs across a week boundary counts for both weeks. The table adds, for context, the average hours per day and the single worst day of that week.',
        byCategory: {
            caution: {
                chartTitle: 'Caution Exposure Probability by Week (%)',
                eventPhrase: 'at least one stretch of days at Caution level or above (HI ≥ 80°F) lasting',
            },
            extremeCaution: {
                chartTitle: 'Extreme Caution Exposure Probability by Week (%)',
                eventPhrase: 'at least one stretch of days at Extreme Caution level or above (HI ≥ 91°F) lasting',
            },
            danger: {
                chartTitle: 'Danger Exposure Probability by Week (%)',
                eventPhrase: 'at least one stretch of days at Danger level or above (HI ≥ 104°F) lasting',
            },
            extremeDanger: {
                chartTitle: 'Extreme Danger Exposure Probability by Week (%)',
                eventPhrase: 'at least one stretch of days at Extreme Danger level (HI ≥ 126°F) lasting',
            },
        },
        detailColumns: [
            { key: 'mean_daily_hours', label: 'Mean hours/day', decimals: 1 },
            { key: 'record_daily_hours.value', yearKey: 'record_daily_hours.year', label: 'Worst day (h)', decimals: 0 },
        ],
        csvColumns: [
            { path: 'mean_daily_hours', header: 'mean_daily_hours' },
            { path: 'record_daily_hours.value', header: 'record_daily_hours' },
            { path: 'record_daily_hours.year', header: 'record_daily_hours_year' },
        ],
        csvTitle: 'Heat Index — weekly probability',
    },

};

export const thresholdDisplay = (value, presentation) =>
    presentation && presentation.unit ? `${value} ${presentation.unit}` : String(value);
